import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { api } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/images";

const trPost = (post, lang) => {
  const tr = post?.translations || {};
  return { ...(tr.en || {}), ...(tr[lang] || {}) };
};

export default function DiaryPage() {
  const { t, lang } = useSite();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/diary").then((r) => setPosts(r.data || [])).finally(() => setLoading(false));
  }, []);

  const featured = posts.find((p) => p.featured) || posts[0];
  const rest = posts.filter((p) => p !== featured);

  return (
    <div data-testid="diary-page" className="bg-ivory">
      {/* HERO */}
      <section className="relative h-[46vh] min-h-[360px] flex items-end overflow-hidden" data-testid="diary-hero">
        <img src={IMAGES.about} alt="Campania" className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <Reveal className="relative max-w-6xl mx-auto w-full px-6 pb-14">
          <p className="text-[11px] font-medium tracking-[0.3em] text-sand-brand uppercase mb-4" data-testid="diary-eyebrow">{t("diary.eyebrow")}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-ivory font-normal leading-[1.05]">{t("diary.title")}</h1>
          <p className="mt-4 text-base md:text-lg text-ivory/85 max-w-2xl leading-relaxed">{t("diary.sub")}</p>
        </Reveal>
      </section>

      {/* LIST */}
      <section className="px-6 py-20 md:py-24">
        <div className="max-w-6xl mx-auto">
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="diary-loading">
              {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-[380px] rounded-3xl bg-sand/50 animate-pulse" />)}
            </div>
          )}

          {!loading && posts.length === 0 && (
            <p className="text-ocean/60 text-center py-20" data-testid="diary-empty">{t("diary.empty")}</p>
          )}

          {!loading && featured && (
            <Reveal className="mb-14">
              <Link to={`/diary/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center" data-testid={`diary-featured-${featured.slug}`}>
                <div className="lg:col-span-3 relative overflow-hidden rounded-3xl aspect-[16/10]">
                  <img src={featured.image} alt={trPost(featured, lang).title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="lg:col-span-2">
                  <p className="text-[11px] tracking-[0.3em] uppercase text-sea mb-4">{t("diary.eyebrow")}</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-ocean leading-tight">{trPost(featured, lang).title}</h2>
                  <p className="mt-4 text-ocean/70 leading-relaxed">{trPost(featured, lang).excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.25em] uppercase text-ocean group-hover:text-sea transition-colors">
                    {t("diary.readMore")} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </Reveal>
          )}

          {!loading && rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((post) => {
                const tr = trPost(post, lang);
                return (
                  <Reveal key={post.id || post.slug}>
                    <Link
                      to={`/diary/${post.slug}`}
                      className="group block bg-white rounded-3xl overflow-hidden border border-sand/60 shadow-[0_6px_20px_rgba(15,46,62,0.05)] hover:shadow-[0_18px_40px_rgba(15,46,62,0.10)] transition-shadow"
                      data-testid={`diary-card-${post.slug}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img src={post.image} alt={tr.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                      <div className="p-6">
                        {post.tags?.length ? (
                          <p className="text-[10px] tracking-[0.3em] uppercase text-sea mb-3" data-testid={`diary-card-tag-${post.slug}`}>{post.tags[0]}</p>
                        ) : null}
                        <h3 className="font-serif text-xl text-ocean leading-snug">{tr.title}</h3>
                        <p className="mt-2 text-sm text-ocean/65 leading-relaxed line-clamp-3">{tr.excerpt}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.25em] uppercase text-ocean group-hover:text-sea transition-colors">
                          {t("diary.readMore")} <ArrowRight size={13} />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
